'use client';

import React, { useMemo, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRight, Layers, ShieldCheck } from 'lucide-react';
import type { EventActivityRecord } from '@/lib/api';
import {
  activityNavGroupsForModules,
  activityNavHref,
} from '@/components/event-workspace/activity-nav-config';
import { useActivityFrameworkWorkspace } from '@/components/event-workspace/ActivityFrameworkWorkspaceProvider';
import { useEventWorkspace } from '@/components/event-workspace/event-workspace-context';
import { useResolvedActivityId, useResolvedEventId } from '@/lib/resolve-static-param';
import { useActivityChatUnread } from '@/hooks/useActivityChatUnread';
import { SidebarBrandLogo } from '@/components/layout/SidebarBrandLogo';
import { startNavigationLoadingForHref } from '@/lib/navigation-loading';
import { StaticDynamicLink } from '@/lib/static-export-nav';
import { friendlyFormatLabel } from '@/lib/event-format-labels';

function ActivityWorkspaceSidebarComponent() {
  const pathname = usePathname();
  const eventId = useResolvedEventId();
  const activityId = useResolvedActivityId();
  const chatUnread = useActivityChatUnread(activityId);
  const { event } = useEventWorkspace();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { modules, moduleCatalog, activity, frameworkKey, frameworkLabel: fwLabel, loading: modulesLoading, errorStatus, abilities } = useActivityFrameworkWorkspace();
  const sidebarErrorText =
    errorStatus === 401
      ? 'Sign in to access modules'
      : errorStatus === 403
        ? 'Access restricted'
        : errorStatus === 404
          ? 'Activity not found'
          : null;

  const canManageStaff = abilities['activity.team.manage'] === true;

  const matchedActivity = useMemo(() => {
    if (activity) return activity;
    const rawEvent = event as { activities?: Array<Record<string, unknown>> } | null | undefined;
    const list = Array.isArray(rawEvent?.activities) ? rawEvent.activities : [];
    return list.find((a) => a?.id === activityId) || null;
  }, [activity, event, activityId]);

  const navGroups = useMemo(() => {
    const activityType =
      frameworkKey ??
      (matchedActivity?.framework_key as string) ??
      (matchedActivity?.type as string) ??
      (matchedActivity?.category as string) ??
      null;

    const groups = activityNavGroupsForModules(modules, moduleCatalog, activityType ?? matchedActivity);
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => item.key !== 'activity-team' || canManageStaff),
      }))
      .filter((g) => g.items.length > 0);
  }, [modules, moduleCatalog, matchedActivity, frameworkKey, canManageStaff]);

  const eventBase = eventId ? `/dashboard/events/${eventId}` : '/dashboard/events';
  const activitiesHref = `${eventBase}/activities`;
  const activityTitle = (activity?.name as string) ?? 'Activity';
  const eventTitle = typeof event?.title === 'string' && event.title.trim() ? event.title.trim() : 'Event';
  const frameworkLabel = friendlyFormatLabel(
    fwLabel ?? (activity as EventActivityRecord | null)?.framework_label ?? (activity as EventActivityRecord | null)?.framework_key,
    'Activity',
  );

  return (
    <aside className="eh-sidebar flex flex-col h-full w-full overflow-hidden">
      <div className="eh-sidebar-header space-y-3 pb-3 w-full shrink-0">
        <Link href="/" className="block px-5 pt-5 pb-1" aria-label="EnthiosHub home">
          <SidebarBrandLogo />
        </Link>

        {/* Workspace Context Box */}
        <div className="mx-4 rounded-xl border border-white/10 bg-white/5 p-3 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center rounded-md bg-white/10 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-white/80">
              {frameworkLabel}
            </span>
          </div>
          <p className="text-sm font-bold text-white truncate" title={activityTitle}>
            {activityTitle}
          </p>
          {activityTitle.trim().toLowerCase() !== eventTitle.trim().toLowerCase() && (
            <p className="text-[11px] font-medium text-white/60 truncate" title={eventTitle}>
              {eventTitle}
            </p>
          )}
        </div>

        {/* Back Links */}
        <div className="flex flex-col gap-1 px-4 pt-1">
          <StaticDynamicLink
            href={activitiesHref}
            className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white/70 hover:text-white hover:bg-white/5 transition flex items-center gap-2"
          >
            <ChevronRight className="h-3.5 w-3.5 rotate-180 text-white/50" />
            <span>All activities</span>
          </StaticDynamicLink>
          <StaticDynamicLink
            href={`${eventBase}/overview`}
            className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white/70 hover:text-white hover:bg-white/5 transition flex items-center gap-2"
          >
            <Layers className="h-3.5 w-3.5 text-white/50" />
            <span>Event workspace</span>
          </StaticDynamicLink>
        </div>
      </div>

      <div
        className="eh-sidebar-scroll custom-scrollbar space-y-4"
        ref={scrollContainerRef}
      >
        {modulesLoading ? (
          <p className="eh-sidebar-muted px-3">Loading modules…</p>
        ) : sidebarErrorText ? (
          <p className="eh-sidebar-muted px-3">{sidebarErrorText}</p>
        ) : navGroups.length === 0 ? (
          <p className="eh-sidebar-muted px-3">No modules available</p>
        ) : (
          navGroups.map((group) => (
            <div key={group.key} className="mb-3">
              {group.label ? (
                <p className="eh-sidebar-section uppercase tracking-wider font-bold">
                  {group.label}
                </p>
              ) : null}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const href = activityNavHref(eventId, activityId, item.slug);
                  const isActive = item.exact
                    ? pathname === href
                    : pathname === href || Boolean(pathname?.startsWith(`${href}/`));
                  const Icon = item.icon;

                  return (
                    <StaticDynamicLink
                      key={item.key}
                      href={href}
                      onClick={() => startNavigationLoadingForHref(href, pathname)}
                      className={`eh-sidebar-link group ${
                        isActive ? 'eh-sidebar-link--active' : ''
                      }`}
                    >
                      <Icon className="eh-sidebar-link-icon group-hover:text-text-inverse" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.key === 'chat' && chatUnread > 0 ? (
                        <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-text-inverse">
                          {chatUnread > 99 ? '99+' : chatUnread}
                        </span>
                      ) : null}
                      {isActive ? <ChevronRight className="h-3 w-3 shrink-0 text-text-inverse/60" /> : null}
                    </StaticDynamicLink>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="eh-sidebar-footer shrink-0 p-3 pb-5">
        <div className="eh-sidebar-footer-badge justify-center">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-white/80" />
          <span className="truncate">Sub-event workspace</span>
        </div>
      </div>
    </aside>
  );
}

export const ActivityWorkspaceSidebar = React.memo(ActivityWorkspaceSidebarComponent);
